import { SearchOutlined } from "@ant-design/icons"
import { useCreation, useSetState } from "ahooks-v2"
import { Button, Checkbox, Col, Empty, Input, Row, Spin } from "antd"
import classNames from "classnames"
import type { ForwardRefRenderFunction } from "react"
import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from "react"
import "./index.scss"
// ==============================================
import Popover from "../../Popover"
// ==============================================
const CheckboxGroup = Checkbox.Group
// ==============================================
interface stateVO {
  // 当前选中数据
  selectedOptions: any[]
  // 缓存选中数据
  cacheList: string[]
  showName: string
  visible: boolean
  searchValue: string
}
interface SelectListPopoverVO {
  tabKey: string
  data: {
    options: any[]
  }
  list: any[]
  // placeholder默认值
  defaultShowName: string
  // class
  className?: string
  // 组件显隐
  visible?: boolean
  // 是否禁用
  disabled?: boolean
  // 加载中
  loading?: boolean
  // 字段
  nameField?: string
  idField?: string
  // 补充项
  addItemRender?: ((info: any) => React.ReactNode | null) | null
  // 向父组件传递searchValue
  passSearchValue?: (value: string) => void
  // 传递数据到父组件
  passData?: (key: string, data: any) => void
  // 弹出框visible变化
  onDropdownVisibleChange?: (visible: boolean) => void
}
const SelectListPopover: ForwardRefRenderFunction<any, SelectListPopoverVO> = (
  {
    tabKey,
    data,
    list = [],
    defaultShowName = "",
    className = "",
    visible: moduleVisible = true,
    disabled = false,
    loading = false,
    nameField = "name",
    idField = "id",
    addItemRender = null,
    passSearchValue,
    passData,
    onDropdownVisibleChange,
  },
  ref: any,
) => {
  const [state, set] = useSetState<stateVO>({
    selectedOptions: data?.options?.slice() || [],
    cacheList: data?.options?.slice() || [],
    showName: defaultShowName,
    visible: false,
    searchValue: "",
  })
  const { selectedOptions, cacheList, visible, searchValue, showName } = state

  // CheckboxGroup的value
  const groupValue = useCreation(() => {
    return selectedOptions?.map((item) => item[idField]) || []
  }, [idField, selectedOptions])

  // Checkbox.Group
  const checkboxGroupChange = useCallback(
    (values) => {
      const result = []
      for (const item of list) {
        for (const value of values) {
          if (value === item[idField]) {
            result.push(item)
          }
        }
      }
      set({
        selectedOptions: result,
      })
    },
    [idField, list, set],
  )

  // Checkbox
  const checkboxChange = useCallback(
    (checked, item) => {
      set((prev) => {
        const cacheList = prev.cacheList?.slice() || []
        const ids = cacheList?.map((item) => item[idField]) || []
        if (checked) {
          if (ids.indexOf(item[idField]) < 0) {
            cacheList.push(item)
          }
        } else {
          const index = ids?.indexOf(item[idField])
          if (index > -1) cacheList.splice(index, 1)
        }
        return {
          cacheList,
        }
      })
    },
    [idField, set],
  )

  // 控制Popover
  const togglePopover = useCallback(
    (visible: boolean) => {
      set({
        visible,
      })
      // 弹出框visible变化
      onDropdownVisibleChange && onDropdownVisibleChange(visible)
    },
    [set, onDropdownVisibleChange],
  )

  // 搜索
  const onSearch = useCallback(
    (event) => {
      const value = event.target.value
      set({
        searchValue: value,
      })
      // 向父组件传递searchValue
      passSearchValue && passSearchValue(value)
    },
    [passSearchValue, set],
  )

  // focus
  const onFocus = useCallback(() => {
    // 控制Popover
    togglePopover(true)
  }, [togglePopover])

  // 确定
  const submitClick = useCallback(() => {
    set({
      showName: cacheList?.map((item) => item[nameField])?.join("/") || defaultShowName,
      searchValue: "",
    })
    // 传递数据到父组件
    passData &&
      passData(tabKey, {
        options: cacheList,
      })
    // 控制Popover
    togglePopover(false)
  }, [nameField, cacheList, defaultShowName, tabKey, passData, togglePopover, set])

  // 重置为上一次的数据
  const resetBeforeData = useCallback(() => {
    const copyData = data?.options?.slice() || []
    set({
      selectedOptions: copyData,
      cacheList: copyData,
    })
  }, [data, set])

  // 重置为默认数据
  const resetClick = useCallback(() => {
    set({
      selectedOptions: [],
      cacheList: [],
      searchValue: "",
      showName: defaultShowName,
    })
    // 向父组件传递searchValue
    passSearchValue && passSearchValue("")
  }, [defaultShowName, set, passSearchValue])

  // 设置searchValue的值
  const setSearchValue = useCallback(
    (value) => {
      set({
        searchValue: value,
      })
    },
    [set],
  )

  // list变化时，保存之前已经勾选的数据
  useEffect(() => {
    set((prev) => ({
      selectedOptions: prev.cacheList?.slice() || [],
    }))
  }, [list, set])

  // 打开弹窗时，重置为上一次数据
  useEffect(() => {
    if (visible) {
      resetBeforeData() // 重置为上一次的数据
    }
  }, [visible, resetBeforeData])

  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    resetDefaultData: resetClick,
    resetBeforeData,
    togglePopover,
    setSearchValue,
  }))

  return (
    <Popover
      key={`${tabKey}Popover`}
      visible={visible}
      content={
        <div className="select-list-popover">
          <Spin spinning={loading}>
            <div className="select-list-box">
              {list?.length ? (
                <CheckboxGroup
                  className="theme-checkbox-group"
                  value={groupValue}
                  onChange={checkboxGroupChange}
                >
                  <Row>
                    {list?.map((item) => (
                      <Col span={24} key={item[idField]}>
                        <Checkbox
                          className="theme-checkbox-highlight"
                          value={item[idField]}
                          onChange={(event) => checkboxChange(event.target.checked, item)}
                        >
                          <div className="list-item" title={item[nameField]}>
                            <span className="ellipsis">{item[nameField]}</span>
                            {/* 补充项 */}
                            {addItemRender && addItemRender(item)}
                          </div>
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </CheckboxGroup>
              ) : (
                <Empty className="theme-empty" />
              )}
            </div>
          </Spin>
          <div className="btns">
            <Button className="theme-text-btn reset" type="text" onClick={resetClick}>
              重置
            </Button>
            <div>
              <Button className="theme-cancel-btn cancel" onClick={() => togglePopover(false)}>
                取消
              </Button>
              &nbsp;
              <Button className="theme-primary-btn submit" type="primary" onClick={submitClick}>
                确定
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Input
        allowClear
        className={classNames("theme-input-suffix", className, {
          none: !moduleVisible,
        })}
        title={showName}
        placeholder={showName}
        suffix={<SearchOutlined />}
        style={{ width: 210 }}
        disabled={disabled}
        value={searchValue}
        onChange={onSearch}
        onFocus={onFocus}
      />
    </Popover>
  )
}

export default forwardRef(SelectListPopover)
